'use client';

/**
 * Bell icon + unread badge + dropdown feed. Renders nothing for unauthenticated
 * users. Uses Radix Popover so it works on mobile (tap outside dismisses).
 */

import Link from 'next/link';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import {
  useNotificationsFeed,
  useUnreadCount,
  useMarkRead,
} from '@/hooks/useNotifications';
import type { NotificationItem } from '@/lib/notifications-client';
import { safeInAppPath } from '@/lib/safe-path';
import { NotificationPermissionPrompt } from './NotificationPermissionPrompt';

export function NotificationBell({ className }: { className?: string }) {
  const { isAuthenticated } = useAuth();
  const unread = useUnreadCount({ enabled: isAuthenticated });
  const feed = useNotificationsFeed({ enabled: isAuthenticated });
  const markRead = useMarkRead();

  if (!isAuthenticated) return null;

  const count = unread.data ?? 0;
  const items: NotificationItem[] = (feed.data?.pages ?? []).flatMap((p) => p.items);

  const handleItemClick = (n: NotificationItem) => {
    if (!n.readAt) markRead.mutate({ ids: [n.id] });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
          className={
            className ??
            'relative inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted transition-colors'
          }
        >
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="z-50 w-[360px] max-w-[92vw] rounded-lg border bg-background shadow-lg"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          <button
            type="button"
            onClick={() => markRead.mutate({ all: true })}
            disabled={count === 0 || markRead.isPending}
            className="text-xs text-primary hover:underline disabled:opacity-40 disabled:no-underline"
          >
            Mark all read
          </button>
        </div>
        <NotificationPermissionPrompt
          className="m-3 flex items-start gap-3 rounded-md border p-3 bg-muted/30"
          title="Turn on notifications"
          description="Get instant updates about your applications and payments."
          ctaLabel="Enable"
        />
        <div className="max-h-[60vh] overflow-y-auto divide-y">
          {feed.isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-center text-muted-foreground">
              You&apos;re all caught up.
            </div>
          ) : (
            items.map((n) => {
              const inner = (
                <div
                  className={`flex flex-col gap-1 p-3 hover:bg-muted/40 transition-colors ${
                    n.readAt ? 'opacity-70' : 'bg-primary/5'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {n.title && <span className="text-sm font-medium leading-tight">{n.title}</span>}
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground line-clamp-3">{n.body}</span>
                </div>
              );
              const target = safeInAppPath(n.deepLink);
              return target ? (
                <Link
                  key={n.id}
                  href={target}
                  onClick={() => handleItemClick(n)}
                  className="block"
                >
                  {inner}
                </Link>
              ) : (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleItemClick(n)}
                  className="block w-full text-left"
                >
                  {inner}
                </button>
              );
            })
          )}
        </div>
        {feed.hasNextPage && (
          <div className="border-t p-2">
            <button
              type="button"
              onClick={() => feed.fetchNextPage()}
              disabled={feed.isFetchingNextPage}
              className="w-full text-xs py-1.5 rounded hover:bg-muted disabled:opacity-50"
            >
              {feed.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
